import React, {
  memo, useContext, useEffect, useState,
} from 'react';
import {
  Card, Col, Row,
} from 'antd';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import moment from 'moment';

import CustomSwitch from '../../../components/custom-switch';
import Parameter from '../../../components/parameter';
import MiniChart from '../../../components/custom-chart/MiniChart';
import {
  NoDataIcon,
} from '../../../components/icons';
import {
  getPreferredMeasurementUnit,
  getWorkspaceUrl,
  findMostRecentParameter,
} from '../../../utils';
import NoPlatformData from '../../../components/no-platform-data';
import appConfig, { brandingConfig } from '../../../config';
import Loading from '../../../components/loading';
import { updateSeagullUser } from '../../../services/user.service';
import UserContext from '../../../contexts/UserContext';
import useParameterGroups from '../../../config/useParameterGroups';
import ParameterFilters from '../../../components/parameter/ParameterFilters';
import '../styles.scss';
import DatasetMessage from '../../../components/dataset-message';
import PlatformInfo from './PlatformInfo';

const ParametersList = (props) => {
  const {
    platform,
    isMobile,
    metadata,
    parameters,
    onGetChartData,
    isLoading,
    isPlatformsConsole,
    collectionStatus,
    obsLatestDataResult,
  } = props;
  const shouldRenderParamChart = !isPlatformsConsole;
  const shouldRenderHorizontalRule = !isPlatformsConsole;
  const isSamplingLocation = !isPlatformsConsole;

  const { parameterGroups } = useParameterGroups();

  const [categorySelected, setCategorySelected] = useState('');
  const [categoryArray, setCategoryArray] = useState([]);
  const [dataView, setDataView] = useState(true);

  const onDetailChange = (checked) => {
    setDataView(!checked);
  };

  const externalIdType = platform?.obs_dataset_platform_assignment?.platform?.external_id_type;

  // Filter out any parameters that are older than appConfig.oldDataCutoffInHours,
  // except for ESP sites.
  const activeParameters = externalIdType !== 'esp_site'
    ? parameters.filter((parameter) => {
      const timeSince = moment().diff(moment(parameter.date), 'hours');
      return timeSince < appConfig.oldDataCutoffInHours && !Number.isNaN(timeSince);
    })
    : parameters;

  const { seagullUser, unitPreferences, setSeagullUser } = useContext(UserContext);

  useEffect(() => {
    const updatedUser = {
      ...seagullUser,
      unit_preferences: unitPreferences,
    };
    updateSeagullUser(updatedUser)
      .then((data) => {
        setSeagullUser(data);
      })
      .catch((e) => {
        // eslint-disable-next-line no-console
        console.error(e);
      });
  }, [unitPreferences]);

  const onCategoriesChange = (category) => {
    setCategorySelected(category);
  };

  const platformObsData = obsLatestDataResult?.find(({ obs_dataset_id: obsDatasetId }) => obsDatasetId === platform?.obs_dataset_id);
  // TODO: only call findMostRecentParameter once
  const mostRecentParameterTimestamp = findMostRecentParameter(platformObsData)?.observations[0].timestamp;
  const mostRecentParameterId = findMostRecentParameter(platformObsData)?.parameter_id;

  const workspaceUrl = getWorkspaceUrl(mostRecentParameterTimestamp, platformObsData?.obs_dataset_id, mostRecentParameterId);

  const getParameterWidgets = (parameters) => {
    // Ignore parameters with NaN values so the depth labels get rendered at depth transitions correctly
    const filteredParameters = parameters.filter((parameter) => {
      // Trying to match
      // components/custom-map/components/DatasetDetail:getParameterWidgets
      // Repeating some of the above logic towards a future refactor
      // date to be determined...
      const timeSince = moment().diff(moment(parameter.date), 'hours');
      if ((!isSamplingLocation && timeSince > appConfig.oldDataCutoffInHours && externalIdType !== 'esp_site')
        || Number.isNaN(timeSince)
        || Number.isNaN(parameter.value)) {
        return false;
      }
      if (!categorySelected) {
        return parameter;
      }
      const parameterGroup = parameterGroups.find((group) => group.group_name === categorySelected);
      // No filter no problem
      if (!parameterGroup) {
        return parameter;
      }
      // If we are... higher level filters will filter non-sampling old data out already...
      if (parameterGroup.parameter_standard_names?.includes(parameter.standard_name)) {
        return parameter;
      }
      return false;
    });

    parameters.forEach((parameter) => {
      parameterGroups?.forEach((group) => {
        if (group.parameter_standard_names?.includes(parameter.standard_name) && !categoryArray.includes(group.group_name)) {
          setCategoryArray([...categoryArray, group.group_name]);
        }
      });
    });

    return filteredParameters.map((parameter, index) => {
      const previousParameter = filteredParameters[index - 1];

      const getParameterColor = () => {
        const parameterGroup = parameterGroups.find((group) => group.parameter_standard_names.includes(parameter.standard_name));

        if (!parameterGroup?.unSelectedColor) {
          return '#fff';
        }

        return parameterGroup.unSelectedColor;
      };

      const parameterColor = getParameterColor();

      let parameterUrl = `/data-console/${platform.obs_dataset_id}/parameter/${parameter.parameter_id}`;

      if (collectionStatus) {
        parameterUrl = `/data-console/${platform.obs_dataset_id}/parameter/${parameter.parameter_id}?collectionStatus=${collectionStatus}`;
      }

      return (
        <React.Fragment key={index}>
          {(!previousParameter || (parameter.depth !== previousParameter.depth)) && shouldRenderHorizontalRule
            && (
              <Col span={24} key={`depth-group-${parameter.depthGroup}`}>
                <div style={{ display: 'flex', alignItems: 'center', color: '#fff' }}>
                  {!parameter.depth || parameter.depth === 0
                    ? <span style={{ marginRight: 15 }}>SURFACE</span>
                    : (
                      <span style={{ marginRight: 15, whiteSpace: 'nowrap' }}>
                        {getPreferredMeasurementUnit(parameter.depth)}
                        {' '}
                        DEPTH
                      </span>
                    )}
                  <div style={{ backgroundColor: isMobile ? brandingConfig.colors.grey : brandingConfig.colors.greyDark, height: 1, width: '100% ' }} />
                </div>
              </Col>
            )}
          <Col
            key={index}
            md={12}
            xs={24}
          >
            <Card
              className={classnames('parameter-card')}
              style={{
                borderStyle: 'none', borderRadius: 10, paddingBottom: 0, backgroundColor: isMobile ? '#081c2c' : brandingConfig.colors.primaryDark2,
              }}
              headStyle={{ border: 'none', paddingBottom: 0 }}
              bodyStyle={isMobile ? { padding: '0 0' } : { paddingTop: 0, paddingRight: 0 }}
            >
              <Link
                style={{
                  cursor: 'pointer',
                  display: 'flex',
                  color: '#fff',
                }}
                to={parameterUrl}
              >
                <Parameter
                  date={parameter.date}
                  name={parameter.name}
                  value={parameter.value}
                  unit={parameter.unit}
                  dataVerifiedVisible={false}
                  metricVisible={false}
                  style={isMobile ? { paddingTop: 5, paddingBottom: 5, flex: '0 0 60%' } : { flex: '0 0 60%' }}
                  pinned={parameter.pinned}
                  standardName={parameter.standard_name}
                  isMobile={isMobile}
                  isSnapshot
                />
                {parameter?.standard_name !== 'wind_from_direction' && shouldRenderParamChart
                  && (<MiniChart data={[{ data: onGetChartData(parameter) }]} color1={parameterColor} color2={brandingConfig.colors.primary} style={isMobile ? { marginTop: '20px' } : { flex: '0 0 40%' }} />)}
              </Link>
            </Card>
          </Col>
        </React.Fragment>
      );
    });
  };

  return (
    <div
      className="parameter-list"
      style={{
        paddingTop: 20,
        display: 'flex',
        flexDirection: 'column',
        height: '90%',
        maxHeight: '100%',
      }}
    >
      {/* TODO: this should really belong to ConsoleDetail component */}
      {isMobile
        ? (
          <div style={{ position: 'relative', paddingLeft: 10, paddingRight: 10 }}>
            <CustomSwitch
              defaultChecked={!dataView}
              unCheckedChildren="Data"
              checkedChildren="Platform Info"
              className="detail-switch"
              onChange={onDetailChange}
            />
          </div>
        ) : ''}
      {isMobile && platform.obs_dataset_message?.message && (
        <DatasetMessage message={platform.obs_dataset_message?.message} />
      )}
      <Row wrap={false} className="parameter-list-inner" style={{ backgroundColor: brandingConfig.colors.primaryDark2 }}>
        <Col
          className="parameter-list-detail"
          hidden={isMobile && !dataView}
          flex={1}
          md={16}
          xs={24}
          style={!isMobile ? {
            backgroundColor: brandingConfig.colors.primaryDark2,
            borderTopLeftRadius: 10,
            borderBottomLeftRadius: 10,
          } : {
            paddingLeft: 10, paddingRight: 10, paddingBottom: 20, backgroundColor: brandingConfig.colors.primaryDark,
          }}
        >
          {!isMobile && platform.obs_dataset_message?.message && (
            <DatasetMessage message={platform.obs_dataset_message?.message} />
          )}
          <div
            style={{
              paddingTop: 20,
            }}
          >
            <ParameterFilters onCategoriesChange={onCategoriesChange} categorySelected={categorySelected} categoriesPresent={categoryArray} />
          </div>
          <div style={isMobile ? { padding: '0px' } : { padding: '20px' }}>
            <Row gutter={[isMobile ? 0 : 16, 16]}>
              {isLoading ? (
                <Loading />
              ) : (
                <>
                  {activeParameters && activeParameters.length > 0 && (
                    getParameterWidgets(activeParameters)
                  )}
                  {activeParameters.length < 1 && (
                    <NoPlatformData
                      icon={<NoDataIcon />}
                      message={(
                        <>
                          {platform.platform_event === 'recovered'
                            ? <p>It looks like this platform has been recovered for the season.</p>
                            : <p>This platform doesn&apos;t appear to be sending us data.</p>}
                          <p>
                            You can access the non real time data for this platform
                            {' '}
                            <Link to={workspaceUrl}>here</Link>
                            .
                          </p>
                        </>
                      )}
                    />
                  )}
                </>
              )}
            </Row>
          </div>
        </Col>
        <Col
          hidden={isMobile && dataView}
          flex={1}
          md={8}
          xs={24}
          className="bg-dark parameter-list-sidebar"
          style={{
            padding: 20,
            borderTopRightRadius: isMobile ? 0 : 10,
            borderBottomRightRadius: isMobile ? 0 : 10,
          }}
        >
          <PlatformInfo
            platform={platform}
            isMobile={isMobile}
            metadata={metadata}
            isPlatformsConsole={isPlatformsConsole}
          />
        </Col>
      </Row>
    </div>
  );
};

ParametersList.propTypes = {
  isLoading: PropTypes.bool,
  isMobile: PropTypes.bool,
  isPlatformsConsole: PropTypes.bool,
  metadata: PropTypes.array,
  onGetChartData: PropTypes.func,
  platform: PropTypes.object,
  parameters: PropTypes.array,
  collectionStatus: PropTypes.string,
  obsLatestDataResult: PropTypes.array,
};

ParametersList.defaultProps = {
  isMobile: false,
  isPlatformsConsole: false,
  metadata: [],
  parameters: [],
  platform: {},
  onGetChartData: () => { },
  isLoading: true,
  collectionStatus: '',
  obsLatestDataResult: [],
};

export default memo(ParametersList);
